import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { 
  Lightning, 
  Bell, 
  CurrencyCircleDollar, 
  PaperPlaneRight,
  CheckCircle,
  Clock,
  Circle,
  Upload,
  WarningCircle,
  ArrowRight
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';

const DashboardPage = () => {
  const { axiosInstance } = useAuth();
  const [stats, setStats] = useState(null);
  const [wizard, setWizard] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDashboardData = useCallback(async () => {
    try {
      const [statsRes, wizardRes] = await Promise.all([
        axiosInstance.get('/dashboard/stats'),
        axiosInstance.get('/dashboard/wizard-progress'),
      ]);
      setStats(statsRes.data);
      setWizard(wizardRes.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [axiosInstance]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-[#0A2540] border-r-transparent"></div>
          <p className="mt-4 text-sm text-slate-600">Caricamento statistiche...</p>
        </div>
      </div>
    );
  }

  const kpiCards = [
    {
      title: 'POD Attivi',
      value: stats?.total_pods || 0,
      icon: Lightning,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
    },
    {
      title: 'Accise Annue',
      value: `€${(stats?.total_annual_excise || 0).toLocaleString('it-IT')}`,
      icon: CurrencyCircleDollar,
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50',
      borderColor: 'border-indigo-200',
    },
    {
      title: 'Province Attive',
      value: stats?.active_provinces || 0,
      icon: PaperPlaneRight,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
    },
    {
      title: 'Alert Non Letti',
      value: stats?.unread_alerts || 0,
      icon: Bell,
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
    },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-4xl font-extrabold tracking-tighter text-slate-900">
          Dashboard
        </h1>
        <p className="mt-2 text-sm text-slate-600">
          Panoramica generale delle accise energetiche
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {kpiCards.map((kpi) => {
          const Icon = kpi.icon;
          return (
            <div
              key={kpi.title}
              className="bg-white border border-slate-200 rounded-sm p-6 hover:-translate-y-[1px] hover:shadow-md hover:border-slate-300 transition-all"
              data-testid={`kpi-card-${kpi.title.replace(/\s+/g, '-').toLowerCase()}`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs tracking-[0.2em] uppercase font-semibold text-slate-500">
                    {kpi.title}
                  </p>
                  <p className="mt-3 text-3xl font-bold tracking-tighter text-slate-900">
                    {kpi.value}
                  </p>
                </div>
                <div className={`${kpi.bgColor} ${kpi.borderColor} border p-2 rounded-sm`}>
                  <Icon size={20} weight="regular" className={kpi.color} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {wizard && wizard.steps && wizard.steps.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="wizard-progress">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold tracking-tight text-slate-900">Avanzamento Operativo</h2>
            <span className="text-sm font-semibold text-[#0066FF]">{wizard.completion_percentage}%</span>
          </div>
          <div className="w-full bg-slate-100 rounded-full h-2 mb-5">
            <div
              className="bg-[#0066FF] h-2 rounded-full transition-all duration-500"
              style={{ width: `${wizard.completion_percentage}%` }}
            ></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
            {wizard.steps.map((step) => {
              const done = step.status === 'completed';
              return (
                <div key={step.id} className={`flex items-center gap-2 p-3 rounded-sm border ${done ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'}`}>
                  {done ? (
                    <CheckCircle size={18} weight="fill" className="text-green-600 flex-shrink-0" />
                  ) : (
                    <Circle size={18} className="text-slate-400 flex-shrink-0" />
                  )}
                  <div>
                    <p className={`text-xs font-medium ${done ? 'text-green-800' : 'text-slate-600'}`}>{step.name}</p>
                    {step.description && <p className="text-[10px] text-slate-500">{step.description}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold tracking-tight text-slate-900">
                Analisi Costi Energetici
              </h2>
              <p className="text-sm text-slate-600 mt-1">Dettaglio materia prima e oneri</p>
            </div>
            <Lightning size={24} className="text-slate-400" />
          </div>

          <div className="h-64 flex items-center justify-center border border-slate-100 rounded-sm bg-slate-50">
            <p className="text-sm text-slate-500">Grafico in arrivo - Importa dati per visualizzare</p>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm p-6">
          <h2 className="text-xl font-semibold tracking-tight text-slate-800 mb-4">
            Azioni Rapide
          </h2>

          <div className="space-y-3">
            <Link
              to="/pods/import"
              className="block p-4 border border-slate-200 rounded-sm hover:bg-slate-50 hover:border-slate-300 transition-all group"
              data-testid="quick-action-import"
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-700 group-hover:text-slate-900">
                  Import Anagrafica POD
                </span>
                <ArrowRight size={16} className="text-slate-400 group-hover:text-slate-600" />
              </div>
            </Link>

            <Link
              to="/billing/import"
              className="block p-4 border border-slate-200 rounded-sm hover:bg-slate-50 hover:border-slate-300 transition-all group"
              data-testid="quick-action-billing"
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-700 group-hover:text-slate-900">
                  Import Dettaglio Fatturato
                </span>
                <ArrowRight size={16} className="text-slate-400 group-hover:text-slate-600" />
              </div>
            </Link>

            <Link
              to="/communications/provinces"
              className="block p-4 border border-slate-200 rounded-sm hover:bg-slate-50 hover:border-slate-300 transition-all group"
              data-testid="quick-action-pec"
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-700 group-hover:text-slate-900">
                  Comunicazioni UADM
                </span>
                <ArrowRight size={16} className="text-slate-400 group-hover:text-slate-600" />
              </div>
            </Link>

            <Link
              to="/settings"
              className="block p-4 border border-slate-200 rounded-sm hover:bg-slate-50 hover:border-slate-300 transition-all group"
              data-testid="quick-action-settings"
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-700 group-hover:text-slate-900">
                  Configurazione Aziendale
                </span>
                <ArrowRight size={16} className="text-slate-400 group-hover:text-slate-600" />
              </div>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;